import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { MdOutlineFactCheck, MdOutlineWarningAmber } from 'react-icons/md';
import { useToast } from '@podus/ui';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import ConfirmModal from '@/components/ConfirmModal';
import { api, ApiError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import type { AdminReferral, Pagination, ReferralStatus } from '@/types';

const TABS: { label: string; value: ReferralStatus | 'ALL' }[] = [
    { label: 'Needs review', value: 'PENDING_REVIEW' },
    { label: 'Confirmed', value: 'CONFIRMED' },
    { label: 'Voided', value: 'VOIDED' },
    { label: 'All', value: 'ALL' },
];

interface PendingAction {
    referral: AdminReferral;
    action: 'confirm' | 'void' | 'reassign';
}

/** Attribution review queue — confirm, void or reassign, always with a reason (FR-37/45). */
export default function ReferralReview() {
    const toast = useToast();

    const [rows, setRows] = useState<AdminReferral[]>([]);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [page, setPage] = useState(1);
    const [tab, setTab] = useState<ReferralStatus | 'ALL'>('PENDING_REVIEW');
    const [loading, setLoading] = useState(true);

    const [pending, setPending] = useState<PendingAction | null>(null);
    const [targetId, setTargetId] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                page: String(page),
                limit: '20',
                ...(tab !== 'ALL' ? { status: tab } : {}),
            });
            const res = await api.get<{ data: AdminReferral[]; pagination: Pagination }>(
                `/ambassador/admin/referrals?${query.toString()}`,
            );
            setRows(res.response.data);
            setPagination(res.response.pagination);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not load referrals');
        } finally {
            setLoading(false);
        }
    }, [page, tab, toast]);

    useEffect(() => {
        void load();
    }, [load]);

    const submit = async (reason: string) => {
        if (!pending) return;

        if (pending.action === 'reassign' && !targetId.trim()) {
            toast.error('Enter the ambassador ID to reassign to');
            return;
        }

        setBusy(true);
        try {
            await api.patch(`/ambassador/admin/referrals/${pending.referral.id}`, {
                action: pending.action,
                reason,
                ...(pending.action === 'reassign' ? { ambassadorId: Number(targetId) } : {}),
            });
            toast.success('Referral updated');
            setPending(null);
            setTargetId('');
            await load();
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not update that referral');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-8">
            <h2 className="text-[22px] font-bold text-secondary">Attribution review</h2>
            <p className="mt-1 text-[13px] text-[#747474]">
                Flagged referrals are held out of headline totals until you resolve them. Every decision is recorded in
                the audit log.
            </p>

            <div className="mt-5 flex items-center gap-1 border-b border-[#EEEEEE]">
                {TABS.map((item) => (
                    <button
                        key={item.value}
                        type="button"
                        onClick={() => {
                            setTab(item.value);
                            setPage(1);
                        }}
                        className={`-mb-px border-b-2 px-3 py-2.5 text-[14px] transition-colors ${
                            tab === item.value
                                ? 'border-primary font-bold text-secondary'
                                : 'border-transparent text-[#9a9a9a] hover:text-[#6b6b6b]'
                        }`}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            <div className="mt-5">
                {loading ? (
                    <div className="flex flex-col gap-2">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div key={index} className="skeleton-shimmer h-20 w-full rounded-lg" />
                        ))}
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        Icon={MdOutlineFactCheck}
                        title={tab === 'PENDING_REVIEW' ? 'Nothing needs review' : 'No referrals here'}
                        description={
                            tab === 'PENDING_REVIEW'
                                ? 'Referrals only land here when a fraud heuristic flags them. An empty queue is a good sign.'
                                : 'Try a different tab.'
                        }
                    />
                ) : (
                    <ul className="flex flex-col gap-3">
                        {rows.map((row) => (
                            <li key={row.id} className="rounded-xl border border-[#E0E0E0] bg-white p-5">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <StatusBadge status={row.status} />
                                            <span className="font-mono text-[12px] text-[#9a9a9a]">
                                                #{row.id} · {row.code ?? 'no code'}
                                            </span>
                                        </div>

                                        <p className="mt-2 text-[14px] text-secondary">
                                            <strong>{row.referredUser?.name || 'Unknown user'}</strong>{' '}
                                            <span className="text-[#9a9a9a]">
                                                ({row.referredUser?.maskedEmail ?? '—'})
                                            </span>{' '}
                                            signed up on {formatDate(row.attributedAt ?? row.createdAt)}
                                        </p>

                                        <p className="mt-1 text-[13px] text-[#747474]">
                                            Credited to{' '}
                                            {row.ambassador ? (
                                                <Link
                                                    to={`/admin/ambassadors/${row.ambassador.id}`}
                                                    className="font-semibold text-secondary hover:text-primary"
                                                >
                                                    {row.ambassador.name ?? row.ambassador.email}
                                                </Link>
                                            ) : (
                                                '—'
                                            )}
                                        </p>

                                        {row.flagReason && (
                                            <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-[12px] leading-5 text-amber-800">
                                                <MdOutlineWarningAmber size={15} className="mt-0.5 shrink-0" />
                                                {row.flagReason}
                                            </p>
                                        )}

                                        {row.voidedReason && (
                                            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-700">
                                                Voided: {row.voidedReason}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                                        {row.status !== 'CONFIRMED' && (
                                            <button
                                                type="button"
                                                onClick={() => setPending({ referral: row, action: 'confirm' })}
                                                className="rounded-lg bg-secondary px-4 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
                                            >
                                                Confirm
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setPending({ referral: row, action: 'reassign' })}
                                            className="rounded-lg border border-[#E0E0E0] px-4 py-2 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5]"
                                        >
                                            Reassign
                                        </button>
                                        {row.status !== 'VOIDED' && (
                                            <button
                                                type="button"
                                                onClick={() => setPending({ referral: row, action: 'void' })}
                                                className="rounded-lg border border-red-200 px-4 py-2 text-[13px] font-semibold text-red-600 transition-colors hover:bg-red-50"
                                            >
                                                Void
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {pagination && pagination.totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                        <p className="text-[13px] text-[#747474]">
                            Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="rounded-lg border border-[#E0E0E0] px-3 py-1.5 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                disabled={page >= pagination.totalPages}
                                onClick={() => setPage((p) => p + 1)}
                                className="rounded-lg border border-[#E0E0E0] px-3 py-1.5 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ConfirmModal
                open={pending !== null}
                title={
                    pending?.action === 'confirm'
                        ? 'Confirm this referral?'
                        : pending?.action === 'void'
                          ? 'Void this referral?'
                          : 'Reassign this referral?'
                }
                description={
                    pending?.action === 'confirm'
                        ? 'It will start counting toward the ambassador’s headline totals.'
                        : pending?.action === 'void'
                          ? 'It stops counting anywhere and is hidden from the ambassador’s table.'
                          : 'Credit moves to another ambassador, using their current referral code.'
                }
                confirmLabel={
                    pending?.action === 'confirm' ? 'Confirm' : pending?.action === 'void' ? 'Void' : 'Reassign'
                }
                destructive={pending?.action === 'void'}
                requireReason
                reasonLabel="Reason (recorded in the audit log)"
                busy={busy}
                extra={
                    pending?.action === 'reassign' ? (
                        <div>
                            <label htmlFor="reassign-target" className="block text-[13px] font-semibold text-secondary">
                                Reassign to ambassador ID <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="reassign-target"
                                type="number"
                                value={targetId}
                                onChange={(e) => setTargetId(e.target.value)}
                                placeholder="e.g. 42"
                                className="mt-1.5 w-full rounded-lg border border-[#D2D2D2] bg-gray-50 px-4 py-3 text-[14px] outline-none transition-colors focus:border-[#151515] focus:bg-white focus:ring-2 focus:ring-[#151515]"
                            />
                            <p className="mt-1.5 text-[12px] text-[#9a9a9a]">
                                Find the ID in the URL of an ambassador&rsquo;s detail page.
                            </p>
                        </div>
                    ) : undefined
                }
                onConfirm={submit}
                onClose={() => {
                    setPending(null);
                    setTargetId('');
                }}
            />
        </div>
    );
}
